import {
  AgentChatMessage,
  AgentRole,
  AgentType,
  GetAgentThreadRun200Response,
  SaveMemoryDecision,
} from "@coeff/api";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { isAxiosError } from "../../utils/axios";

import {
  CREATE_MODE_DASHBOARD_POLL_TIMEOUT_MS,
  useGetAgentThreadRun,
  useGetAgentThreads,
  usePostAgentThreadMutation,
  useSuperAdminViewEditorThreadMessages,
} from "./webDashboard";

export type ChatMode = "editor" | "viewer" | "builder" | "superAdminHistory";

export interface Message {
  id: string;
  content: {
    text?: string;
    questions?: AgentChatMessage["content"]["questions"];
    save_memory_confirmation?: AgentChatMessage["content"]["save_memory_confirmation"];
    created_custom_theme?: AgentChatMessage["content"]["created_custom_theme"];
    theme_request_form?: AgentChatMessage["content"]["theme_request_form"];
    reasoning?: string;
    tool_calls?: AgentChatMessage["content"]["tool_calls"];
    web_dashboard_json?: AgentChatMessage["content"]["web_dashboard_json"];
    dashboard_updated?: boolean;
    web_dashboard_plan?: AgentChatMessage["content"]["web_dashboard_plan"];
    tool_call_summary_label?: string;
    pause_terminal?: boolean;
    screenshot?: AgentChatMessage["content"]["screenshot"];
    event_type?: AgentChatMessage["content"]["event_type"];
    revert_to_message_id?: string;
    web_dash_version_s3_path?: string;
    // Backward/forward-compatible: may be present on user messages (server-derived).
    auto_fix?: any;
  };
  role: "user" | "agent" | "event";
  thread_id: string;
  created_dt: string;
}

export type RunStatus = "idle" | "queued" | "in_progress" | "completed" | "failed";

interface PriorMessage {
  role: "user" | "agent";
  content: string;
}

export class NeedsGoogleOAuthError extends Error {
  messageToSend: string;
  priorMessages?: PriorMessage[];
  meta?: any;
  chatMode: ChatMode;
  threadId?: string;

  constructor(args: {
    messageToSend: string;
    priorMessages?: PriorMessage[];
    meta?: any;
    chatMode: ChatMode;
    threadId?: string;
  }) {
    super("needs_oauth");
    this.name = "NeedsGoogleOAuthError";
    this.messageToSend = args.messageToSend;
    this.priorMessages = args.priorMessages;
    this.meta = args.meta;
    this.chatMode = args.chatMode;
    this.threadId = args.threadId;
  }
}

export class DashboardArchivedError extends Error {
  constructor(message = "This dashboard is archived. Unarchive it to use AI chat.") {
    super(message);
    this.name = "DashboardArchivedError";
  }
}

function getThreadStorageKey(args: { webDashboardId: string; agentType: string }): string {
  return `web_dashboard_thread_id:${args.webDashboardId}:${args.agentType}`;
}

export interface UseChatMessagesOptions {
  mode: ChatMode;
  webDashboardId: string;
  spreadsheetId: string;
  sourceType?: string | null;
  enabled: boolean;
  initialThreadId?: string | null;
}

export interface UseChatMessagesResult {
  messages: Message[];
  isLoading: boolean;
  isLoadingExistingThreads: boolean;
  threadId: string | null;
  runStatus: RunStatus;
  isThinking: boolean;
  setIsThinking: (value: boolean) => void;
  // Super admin specific
  initialVersionS3Path: string | null;
  dashboardCreatedDt: string | null;
  // For regular mode
  submitMessage: (
    message: string,
    priorMessages?: PriorMessage[],
    onSuccess?: () => void,
    meta?: {
      auto_fix?: {
        is_auto_fix: true;
        attempt_index?: number;
        max_attempts?: number;
        source?: "fix_button" | "auto_fix_retry";
      };
      // Confirm/reject decision for a pending save_memory proposal; handled
      // synchronously by the server (no agent run).
      save_memory_decision?: SaveMemoryDecision;
    }
  ) => Promise<string | undefined>;
  // Track processed message IDs for dashboard updates
  processedMessageIds: Set<string>;
  markMessageProcessed: (messageId: string) => void;
}

const EDITOR_AGENT_TYPE = AgentType.WebDashboardEditor;
const VIEWER_AGENT_TYPE = AgentType.WebDashboardViewer;
const BUILDER_AGENT_TYPE = AgentType.WebDashboardBuilder;

const convertAgentChatMessageToMessage = (msg: AgentChatMessage): Message => {
  return {
    id: msg.id,
    content: {
      text: msg.content.text ?? undefined,
      questions: msg.content.questions,
      save_memory_confirmation: msg.content.save_memory_confirmation,
      created_custom_theme: msg.content.created_custom_theme,
      theme_request_form: msg.content.theme_request_form,
      reasoning: msg.content.reasoning ?? undefined,
      tool_calls: msg.content.tool_calls,
      web_dashboard_json: msg.content.web_dashboard_json,
      dashboard_updated: msg.content.dashboard_updated ?? undefined,
      web_dashboard_plan: msg.content.web_dashboard_plan,
      tool_call_summary_label: msg.content.tool_call_summary_label ?? undefined,
      pause_terminal: msg.content.pause_terminal ?? undefined,
      screenshot: msg.content.screenshot ?? undefined,
      event_type: msg.content.event_type,
      revert_to_message_id: msg.content.revert_to_message_id ?? undefined,
      web_dash_version_s3_path: msg.content.web_dash_version_s3_path ?? undefined,
      auto_fix: (msg.content as any)?.auto_fix,
    },
    role: msg.role === AgentRole.User ? "user" : msg.role === AgentRole.Event ? "event" : "agent",
    thread_id: msg.thread_id,
    created_dt: msg.created_dt,
  };
};

export function useChatMessages(options: UseChatMessagesOptions): UseChatMessagesResult {
  const { mode, webDashboardId, spreadsheetId, sourceType, enabled, initialThreadId } = options;

  const isSuperAdminMode = mode === "superAdminHistory";
  const isBuilderMode = mode === "builder";
  const agentType =
    mode === "viewer" ? VIEWER_AGENT_TYPE : isBuilderMode ? BUILDER_AGENT_TYPE : EDITOR_AGENT_TYPE;

  // State
  const [messages, setMessages] = useState<Message[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>(undefined);
  const [processedMessageIds, setProcessedMessageIds] = useState<Set<string>>(new Set());
  const hasCompletedInitialLoadRef = useRef(false);
  const builderThreadPollStartTimeRef = useRef<number | null>(null);

  // Persist the last-used thread id so we don't accidentally "switch" threads
  // when multiple exist (common after OAuth round-trips or earlier bugs).
  useEffect(() => {
    if (isSuperAdminMode) {
      return;
    }
    if (!currentThreadId) {
      return;
    }
    try {
      localStorage.setItem(getThreadStorageKey({ webDashboardId, agentType }), currentThreadId);
    } catch {
      // ignore storage failures
    }
  }, [isSuperAdminMode, currentThreadId, webDashboardId, agentType]);

  useEffect(() => {
    if (isSuperAdminMode || !initialThreadId || currentThreadId === initialThreadId) {
      return;
    }
    setCurrentThreadId(initialThreadId);
  }, [currentThreadId, initialThreadId, isSuperAdminMode]);

  // Track currentThreadId in a ref so the reset effect can read it without re-triggering.
  const currentThreadIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    currentThreadIdRef.current = currentThreadId;
  }, [currentThreadId]);

  // Reset state when mode changes to prevent stale data from triggering updates.
  // Skip the reset when initialThreadId changes to the thread we're already tracking —
  // this happens in builder mode when the dashboard poll returns agent_thread_id after
  // the existingThreadsQuery already found the thread, and we don't want to wipe messages.
  useEffect(() => {
    if (initialThreadId && currentThreadIdRef.current === initialThreadId) {
      return;
    }
    setMessages([]);
    setIsThinking(false);
    setCurrentThreadId(initialThreadId ?? undefined);
    setProcessedMessageIds(new Set());
    hasCompletedInitialLoadRef.current = false;
    builderThreadPollStartTimeRef.current = null;
  }, [initialThreadId, mode]);

  // ============================================================================
  // SUPER ADMIN MODE: One-time fetch of editor thread history (no polling)
  // ============================================================================
  const superAdminQuery = useSuperAdminViewEditorThreadMessages(
    webDashboardId,
    enabled && isSuperAdminMode
  );

  // ============================================================================
  // REGULAR MODE: Fetch existing threads, then poll for messages
  // ============================================================================

  // Step 1: Find existing threads for this dashboard
  const existingThreadsQuery = useGetAgentThreads(
    agentType,
    spreadsheetId,
    webDashboardId,
    2, // Only need to check if there's 1 or more
    {
      enabled:
        enabled &&
        !isSuperAdminMode &&
        !initialThreadId &&
        Boolean(spreadsheetId && webDashboardId),
    }
  );

  // Step 2: Poll for thread run updates (only when we have a thread and are thinking)
  const threadRunQuery = useGetAgentThreadRun(currentThreadId, agentType, {
    enabled: !isSuperAdminMode && Boolean(currentThreadId),
    refetchInterval: (data: GetAgentThreadRun200Response | undefined) => {
      // Stop polling when completed or failed
      if (data?.run_status === "completed" || data?.run_status === "failed") {
        builderThreadPollStartTimeRef.current = null;
        return false;
      }
      // Poll while the server says the run is still in-flight (including "paused" while stopping).
      if (
        data?.run_status === "in_progress" ||
        data?.run_status === "queued" ||
        data?.run_status === "paused"
      ) {
        if (isBuilderMode) {
          if (builderThreadPollStartTimeRef.current === null) {
            builderThreadPollStartTimeRef.current = Date.now();
          }

          if (
            Date.now() - builderThreadPollStartTimeRef.current >=
            CREATE_MODE_DASHBOARD_POLL_TIMEOUT_MS
          ) {
            return false;
          }
        }

        return 1000;
      }
      builderThreadPollStartTimeRef.current = null;
      return false;
    },
    refetchIntervalInBackground: true,
  });

  // Mutation for posting messages
  const postAgentThreadMutation = usePostAgentThreadMutation();

  const resolveThreadIdForSend = useCallback((): string | undefined => {
    if (currentThreadId) {
      return currentThreadId;
    }
    const threads = existingThreadsQuery.data?.threads || [];
    if (threads.length === 0) {
      return undefined;
    }

    // Prefer the persisted thread id (if it still exists).
    let preferredThreadId: string | null = null;
    try {
      preferredThreadId =
        localStorage.getItem(getThreadStorageKey({ webDashboardId, agentType })) || null;
    } catch {
      preferredThreadId = null;
    }
    if (preferredThreadId) {
      const match = threads.find(t => t.thread_id === preferredThreadId);
      if (match) {
        return match.thread_id;
      }
    }

    // Otherwise use the newest thread (API is requested sort_order=desc).
    return threads[0].thread_id;
  }, [agentType, currentThreadId, existingThreadsQuery.data, webDashboardId]);

  // Initialize thread from existing threads on mount (regular mode only)
  useEffect(() => {
    if (
      isSuperAdminMode ||
      initialThreadId ||
      !existingThreadsQuery.data ||
      currentThreadId !== undefined
    ) {
      return;
    }

    const threads = existingThreadsQuery.data.threads || [];

    if (threads.length === 0) {
      return;
    }

    let preferredThreadId: string | null = null;
    try {
      preferredThreadId =
        localStorage.getItem(getThreadStorageKey({ webDashboardId, agentType })) || null;
    } catch {
      preferredThreadId = null;
    }

    if (preferredThreadId) {
      const match = threads.find(t => t.thread_id === preferredThreadId);
      if (match) {
        setCurrentThreadId(match.thread_id);
        return;
      }
    }

    if (threads.length === 1) {
      setCurrentThreadId(threads[0].thread_id);
    } else {
      const latestThread = threads[0];
      setCurrentThreadId(latestThread.thread_id);

      Sentry.captureMessage(`Multiple agent threads found for web dashboard`, {
        level: "warning",
        extra: {
          web_dashboard_id: webDashboardId,
          spreadsheet_id: spreadsheetId,
          agent_type: agentType,
          thread_count: threads.length,
          thread_ids: threads.map(t => t.thread_id),
          using_thread_id: latestThread.thread_id,
        },
      });
    }
  }, [
    isSuperAdminMode,
    initialThreadId,
    existingThreadsQuery.data,
    currentThreadId,
    webDashboardId,
    spreadsheetId,
    agentType,
  ]);

  // Update messages when thread run data changes (regular mode with polling)
  useEffect(() => {
    // Skip when disabled (sidebar closed) or in super admin mode
    if (!enabled || isSuperAdminMode) {
      return;
    }

    if (threadRunQuery.data?.messages && threadRunQuery.data.messages.length > 0) {
      const isFirstLoad = !hasCompletedInitialLoadRef.current;

      // On initial load, mark all existing messages as "processed" so they don't
      // trigger dashboard updates when the sidebar renders
      if (isFirstLoad) {
        const existingIds = threadRunQuery.data.messages.map(m => m.id);
        setProcessedMessageIds(prev => {
          const next = new Set(prev);
          existingIds.forEach(id => next.add(id));
          return next;
        });
        hasCompletedInitialLoadRef.current = true;
      }

      setMessages(threadRunQuery.data.messages);
    }
  }, [enabled, isSuperAdminMode, threadRunQuery.data]);

  // Update messages when super admin data changes (one-time fetch, no polling)
  useEffect(() => {
    if (!isSuperAdminMode) {
      return;
    }

    if (superAdminQuery.data?.messages) {
      const converted = superAdminQuery.data.messages.map(convertAgentChatMessageToMessage);
      setMessages(converted);
      hasCompletedInitialLoadRef.current = true;
    }
  }, [isSuperAdminMode, superAdminQuery.data]);

  // Update thinking state based on thread run status (regular mode)
  useEffect(() => {
    if (isSuperAdminMode) {
      return;
    }

    const status = threadRunQuery.data?.run_status;
    if (status === "completed" || status === "failed") {
      setIsThinking(false);
    } else if (status === "in_progress" || status === "queued" || status === "paused") {
      setIsThinking(true);
    }
  }, [isSuperAdminMode, threadRunQuery.data?.run_status]);

  // Mark a message as processed (for dashboard update tracking)
  const markMessageProcessed = useCallback((messageId: string) => {
    setProcessedMessageIds(prev => new Set(prev).add(messageId));
  }, []);

  // Submit message (regular mode only - no-op in super admin mode)
  const submitMessage = useCallback(
    async (
      message: string,
      priorMessages?: PriorMessage[],
      onSuccess?: () => void,
      meta?: {
        auto_fix?: {
          is_auto_fix: true;
          attempt_index?: number;
          max_attempts?: number;
          source?: "fix_button" | "auto_fix_retry";
        };
        save_memory_decision?: SaveMemoryDecision;
      }
    ): Promise<string | undefined> => {
      if (isSuperAdminMode) {
        return undefined;
      }

      // IMPORTANT: Treat this as a "new thread" only if we truly have no thread_id to send to.
      // During OAuth reloads we can temporarily have `currentThreadId` unset while
      // `resolveThreadIdForSend()` returns an existing thread. If we incorrectly mark it as
      // "new", we skip initial-load processed-message seeding, and old tool calls (e.g. screenshot
      // requests) can be re-processed on first poll.
      const threadIdForSend = resolveThreadIdForSend();
      const isNewThread = !threadIdForSend;

      let postResponse: any;
      try {
        postResponse = await postAgentThreadMutation.mutateAsync({
          message,
          thread_id: threadIdForSend,
          agent_type: agentType,
          spreadsheet_id: spreadsheetId,
          web_dashboard_id: webDashboardId,
          source_type: sourceType ?? undefined,
          prior_messages: priorMessages,
          auto_fix: meta?.auto_fix,
          save_memory_decision: meta?.save_memory_decision,
        } as any);
      } catch (error) {
        // If the user is signed out (or the backend can't authenticate this request),
        // /agent/thread can return 401/403. Treat this the same as needs_oauth so the
        // viewer can prompt the correct login flow and preserve the pending message.
        if (isAxiosError(error)) {
          const status = error.response?.status;
          if (status === 401 || status === 403) {
            throw new NeedsGoogleOAuthError({
              messageToSend: message,
              priorMessages,
              meta,
              chatMode: mode,
              threadId: threadIdForSend,
            });
          }
        }
        throw error;
      }

      // If we sent against an existing thread, ensure state catches up immediately.
      if (threadIdForSend && !currentThreadId) {
        setCurrentThreadId(threadIdForSend);
      }

      if (postResponse?.status === "archived") {
        throw new DashboardArchivedError();
      }

      if (postResponse?.status === "needs_oauth") {
        throw new NeedsGoogleOAuthError({
          messageToSend: message,
          priorMessages,
          meta,
          chatMode: mode,
          threadId: threadIdForSend ?? postResponse.thread_id,
        });
      }

      if (isNewThread) {
        hasCompletedInitialLoadRef.current = true;
      }

      setCurrentThreadId(postResponse.thread_id);
      onSuccess?.();

      return postResponse.thread_id;
    },
    [
      isSuperAdminMode,
      currentThreadId,
      resolveThreadIdForSend,
      mode,
      agentType,
      spreadsheetId,
      webDashboardId,
      sourceType,
      postAgentThreadMutation,
    ]
  );

  // Compute run status
  const runStatus: RunStatus = useMemo(() => {
    if (isSuperAdminMode) {
      return "completed"; // History is always static/complete
    }
    const status = threadRunQuery.data?.run_status;
    if (status === "queued") {
      return "queued";
    }
    if (status === "in_progress") {
      return "in_progress";
    }
    if (status === "completed") {
      return "completed";
    }
    if (status === "failed") {
      return "failed";
    }
    return "idle";
  }, [isSuperAdminMode, threadRunQuery.data?.run_status]);

  // Compute loading state — use isInitialLoading to avoid false positives from
  // disabled queries (React Query v4 reports isLoading=true for unfetched disabled queries)
  const isLoading = isSuperAdminMode
    ? superAdminQuery.isInitialLoading
    : threadRunQuery.isInitialLoading;

  return {
    messages,
    isLoading,
    isLoadingExistingThreads:
      !isSuperAdminMode &&
      !initialThreadId &&
      !existingThreadsQuery.data &&
      existingThreadsQuery.isInitialLoading,
    threadId: isSuperAdminMode ? superAdminQuery.data?.thread_id ?? null : currentThreadId ?? null,
    runStatus,
    isThinking,
    setIsThinking,
    initialVersionS3Path: isSuperAdminMode
      ? superAdminQuery.data?.initial_version_s3_path ?? null
      : null,
    dashboardCreatedDt: isSuperAdminMode
      ? superAdminQuery.data?.dashboard_created_dt ?? null
      : null,
    submitMessage,
    processedMessageIds,
    markMessageProcessed,
  };
}
